import React from 'react';
import { type TFunction } from 'i18next';
import {
  createMRTColumnHelper,
  MRT_ColumnDef,
  MRT_Row,
} from 'material-react-table';
import { Trans } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router';
import { Box, Palette } from '@mui/material';
import { IconBolt, IconInfoCircle, IconTag } from '@tabler/icons-react';

import {
  Badge as FTBadge,
  Table,
  Tooltip,
  Typography,
} from '@zendesk/forethought-elements';
import OverflowDropdown from '../common/overflow-dropdown/OverflowDropdown';
import { SentimentTooltip } from '../common/SentimentTooltip';
import VariantPill from '../common/VariantPill';
import { INSIGHT_TOOLTIP_COPY, TABLE_COLUMN_TO_LABEL_MAP } from '../constants';
import { colorMap, getReadableNoResponseString } from '../helpers';
import { Scope } from '../types';
import { ReviewStatusIndicator } from './ReviewStatusIndicator';
import uniqBy from 'lodash/fp/uniqBy';
import autonomousAgentBadge from 'src/assets/images/autonomous-agent-badge.svg';
import thumbDown from 'src/assets/images/quick-feedback-config-pics/thumb-down-active.svg';
import thumbUp from 'src/assets/images/quick-feedback-config-pics/thumb-up-active.svg';
import { TrackingEventType } from 'src/components/app/types';
import { getDiscoverSentimentEmoji } from 'src/components/discover-sentiment-emoji/helpers';
import PillButton from 'src/components/pill-button/PillButton';
import { VoiceBrandBadge } from 'src/components/voice-brand-badge';
import { DateCell } from 'src/pages/intent-conversation-analytics/components/DateCell';
import { DeflectionInsightCell } from 'src/pages/intent-conversation-analytics/components/DeflectionInsightCell';
import {
  ResolutionAutomatedLabel,
  ResolutionTierBadge,
} from 'src/pages/intent-conversation-analytics/components/ResolutionTierBadge';
import { SentimentInsightCell } from 'src/pages/intent-conversation-analytics/components/SentimentInsightCell';
import {
  AUTOMATED_CELL_WIDTH,
  NA,
  SMALL_CELL_WIDTH,
} from 'src/pages/intent-conversation-analytics/constants';
import { formatTimeSpent } from 'src/pages/intent-conversation-analytics/ConversationDrawer/utils';
import ConversationTranscript, {
  CellContainer,
} from 'src/pages/intent-conversation-analytics/ConversationTranscript';
import { formatCSAT } from 'src/pages/intent-conversation-analytics/helpers';
import { BreakdownPaletteTooltip } from 'src/pages/workflow-builder/BreakdownPaletteTooltip';
import { HorizontalStackedBar } from 'src/pages/workflow-builder/intent-workflows-table/HorizontalStackedBar';
import WorkflowBadge from 'src/pages/workflow-builder/intent-workflows-table/WorkflowBadge';
import {
  AssistBaseFilterRequest,
  InsightConversation,
  InsightEmailConversation,
  toABTestVariantInfo,
} from 'src/services/insights/types';
import { AppDispatch } from 'src/store/hooks';
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter';
import { Routes } from 'src/utils/enums';
import { formatChannelLabel } from 'src/utils/formatChannelLabel';
import { isDeflectionInsightsProcessing } from 'src/utils/solve/deflectionInsightsUtils';
import { getEffectiveWorkflowMode } from 'src/utils/solve/intentUtils';

export type View = 'chats' | 'topics' | 'articles' | 'workflows' | 'ab_tests';

export type InsightConversationColumns = React.ComponentProps<
  typeof Table<InsightConversation>
>['columns'];

export const buildConversationColumns = (
  t: TFunction<'solve-insights'>,
  palette: Palette,
  getCSATColor: (score?: number | undefined) => string,
  emitTrackingEventCallback: (
    eventType: TrackingEventType,
    additionalParams?: Record<string, unknown>,
  ) => void,
  dispatch: AppDispatch,
  scope: Scope,
  tab: 'topic' | 'article' | 'chat' | 'workflow',
  isCustomMetricsEnabled: boolean,
  channelOptions: { label: string; value: string }[],
  isSlackChannel: boolean,
  isApiChannel: boolean,
  isDetail?: boolean,
  isDeflectionInsightsEnabled?: boolean,
  shouldShowQuickFeedbackAnalytics?: boolean,
  isVoiceChannel?: boolean,
  isSentimentInsightsEnabled?: boolean,
  discoverAnalyticsEnabled?: boolean,
  showInternalBadgeForAnalytics?: boolean,
  isV3Taxonomy?: boolean,
  isVoiceMultiBrandEnabled?: boolean,
  isVoiceOutboundEnabled?: boolean,
  isVoiceCsatEnabled?: boolean,
  hasAiQaPermission?: boolean,
  aiQaRubricNames?: string[],
  abTestConfigId?: string,
  isWebCallChannel?: boolean,
  isHumanEvalsEnabled?: boolean,
  isReviewEnabledChannel?: boolean,
  channelMode?: 'inbound' | 'outbound' | 'inbound_web_call',
  isResolutionUiEnabled?: boolean,
  showProactiveConversations?: boolean,
) => {
  const colorMapping = colorMap(palette);
  // Within a voice mode tab the inbound/outbound split is implicit, so the
  // "Type" column is redundant and direction-specific columns are scoped.
  const isVoiceModeView = Boolean(isVoiceChannel && !isWebCallChannel);
  const isOutboundMode = channelMode === 'outbound';
  // A voice mode tab is active (Inbound/Outbound) — these use explicit,
  // mock-ordered column lists. Web call and legacy combined voice keep the
  // original assembly.
  const isVoiceModeTab =
    isVoiceModeView &&
    (channelMode === 'inbound' || channelMode === 'outbound');
  // Mode-specific labels per the design mocks (image-2 / image-5).
  const transcriptLabel = isOutboundMode
    ? t('chatsTable.outboundCall')
    : t('chatsTable.inboundCall');
  const talkTimeLabel = t('chatsTable.talkTime');
  const columnHelper = createMRTColumnHelper<InsightConversation>();
  const relevanceRating = columnHelper.accessor('relevance_rating', {
    Cell: ({ row }: Row) => {
      return (
        <DeflectionInsightCell
          isProcessing={isDeflectionInsightsProcessing({
            hasDeflectionInsights: !!row.original.relevance_rating,
            isDeflected: row.original.is_deflected,
            modifiedDate: row.original.timestamp,
          })}
          metric={row.original.relevance_rating}
        />
      );
    },
    header: t('chatsTable.relevance'),
    Header: () => (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: '4px',
        }}
      >
        {t('chatsTable.relevance')}
        <Tooltip tooltipContent={INSIGHT_TOOLTIP_COPY.relevance}>
          <IconInfoCircle color={palette.colors.grey[700]} size={20} />
        </Tooltip>
      </Box>
    ),
    size: SMALL_CELL_WIDTH,
  });

  const dropoffRating = columnHelper.accessor('dropoff_rating', {
    Cell: ({ row }: Row) => {
      return (
        <DeflectionInsightCell
          isProcessing={isDeflectionInsightsProcessing({
            hasDeflectionInsights: !!row.original.relevance_rating,
            isDeflected: row.original.is_deflected,
            modifiedDate: row.original.timestamp,
          })}
          metric={row.original.dropoff_rating}
        />
      );
    },
    Header: () => (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: '4px',
        }}
      >
        {t('chatsTable.userEngagement')}
        <Tooltip tooltipContent={INSIGHT_TOOLTIP_COPY.engagement}>
          <IconInfoCircle color={palette.colors.grey[700]} size={20} />
        </Tooltip>
      </Box>
    ),
    header: t('chatsTable.userEngagement'),
    size: 200,
  });

  const resolutionTier = columnHelper.accessor('resolution_tier', {
    Cell: ({ row }: Row) => {
      return (
        <ResolutionTierBadge resolutionTier={row.original.resolution_tier} />
      );
    },
    enableSorting: false,
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.resolution_tier,
    size: SMALL_CELL_WIDTH,
  });

  const resolutionAutomated = columnHelper.display({
    Cell: ({ row }: Row) => {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            ml: '12px',
            width: '100%',
          }}
        >
          <ResolutionAutomatedLabel
            resolutionAutomated={row.original.resolution_automated}
          />
        </Box>
      );
    },
    enableSorting: false,
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.resolution_automated,
    id: 'resolution_automated',
    size: AUTOMATED_CELL_WIDTH,
  });

  // Voice Automated mirrors call eligibility: eligible → Automated,
  // ineligible → Non-automated (replaces the Call eligibility column).
  const voiceResolutionAutomated = columnHelper.display({
    Cell: ({ row }: Row) => {
      const isEligible = row.original.voice_is_outbound
        ? row.original.voice_outbound_answered_by === 'user'
        : row.original.visitor_interacted;

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            ml: '12px',
            width: '100%',
          }}
        >
          <ResolutionAutomatedLabel
            resolutionAutomated={isEligible == null ? null : !!isEligible}
          />
        </Box>
      );
    },
    enableSorting: false,
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.resolution_automated,
    id: 'resolution_automated',
    size: AUTOMATED_CELL_WIDTH,
  });

  const quickFeedbackItem = columnHelper.accessor('useful_count', {
    Cell: ({ row }: Row) => {
      const not_useful_count = row.original.not_useful_count || 0;
      const useful_count = row.original.useful_count || 0;
      const unanswered_count = row.original.unanswered_count || 0;
      const totalFeedback = not_useful_count + useful_count;

      if (totalFeedback < 1) {
        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      }

      return (
        <Box
          sx={{
            flex: 1,
            maxWidth: 140,
            padding: '16px 16px 0 0',
          }}
        >
          <Tooltip
            fullWidth
            maxWidth={320}
            tooltipContent={
              <BreakdownPaletteTooltip
                values={[
                  {
                    color: palette.colors.green[500],
                    label: t('feedbackCell.positive'),
                    value: useful_count,
                  },
                  {
                    color: palette.colors.red[500],
                    label: t('feedbackCell.negative'),
                    value: not_useful_count,
                  },
                  {
                    color: palette.colors.slate[100],
                    label: t('feedbackCell.notAnswered'),
                    value: unanswered_count,
                  },
                ]}
              />
            }
          >
            <HorizontalStackedBar
              data={[
                {
                  color: palette.colors.green[500],
                  value: useful_count,
                },
                {
                  color: palette.colors.red[500],
                  value: not_useful_count,
                },
              ]}
            />
          </Tooltip>
        </Box>
      );
    },
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.useful_count,
    Header: () => {
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '4px',
          }}
        >
          {TABLE_COLUMN_TO_LABEL_MAP.conversation.useful_count}
          <Tooltip
            tooltipContent={
              <Box>
                <Trans
                  components={{ i: <i /> }}
                  i18nKey='chatsTable.quickFeedbackTooltip'
                  t={t}
                />
              </Box>
            }
          >
            <IconInfoCircle color={palette.colors.grey[700]} size={20} />
          </Tooltip>
        </Box>
      );
    },
    size: 250,
  });

  // Single source of truth for the Detected Intent column. menuTitle varies between
  // chat-style channels ('chat') and voice/web_call ('call') so the dropdown copy
  // reads naturally; everything else (cell rendering, sorting, tooltip, tracking)
  // is identical across channels.
  const buildDetectedIntentsColumn = (menuTitle: string) =>
    columnHelper.accessor('detected_intents', {
      Cell: ({ row }: Row) => {
        if (row.original.detected_intents.length === 0) {
          return (
            <Typography color={colorMapping.na} variant='font14'>
              {NA}
            </Typography>
          );
        }

        return (
          <OverflowDropdown
            abTestSamples={row.original.ab_test_samples}
            data={row.original.detected_intents}
            menuTitle={menuTitle}
            scope={scope}
            tab='chat'
            variant='workflow'
          />
        );
      },
      enableSorting: false,
      header: TABLE_COLUMN_TO_LABEL_MAP.conversation.detected_intents,
      Header: () => {
        return (
          <Box
            onMouseEnter={() => {
              emitTrackingEventCallback('insight-tooltip-hover', {
                scope,
                tab,
                table: 'chat',
                value: 'detected_intents',
              });
            }}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '4px',
            }}
          >
            {TABLE_COLUMN_TO_LABEL_MAP.conversation.detected_intents}
            <Tooltip tooltipContent={INSIGHT_TOOLTIP_COPY.chats_workflows}>
              <IconInfoCircle color={palette.colors.grey[700]} size={20} />
            </Tooltip>
          </Box>
        );
      },
      size: 260,
    });

  // ---- Lifted column building blocks ----
  // Each column is a named const so the per-(channel, channel_mode) ordered
  // lists below can assemble them explicitly. Feature-flag gating is expressed
  // as `flag ? colDef : null` and dropped by the final filter.

  const timestampColumn = columnHelper.accessor('timestamp', {
    Cell: ({ row }: Row) => {
      return (
        <Box
          sx={{
            width: SMALL_CELL_WIDTH,
          }}
        >
          <DateCell dateString={row.original.timestamp + 'Z'} />
        </Box>
      );
    },
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.timestamp,
    size: SMALL_CELL_WIDTH,
  });

  const conversationStatusColumn = columnHelper.accessor(
    'conversation_status',
    {
      Cell: ({ row }: Row) => (
        <Typography variant='font14'>
          {row.original.conversation_status === 'in_progress'
            ? t('chatsTable.status.inProgress')
            : t('chatsTable.status.finished')}
        </Typography>
      ),
      enableSorting: false,
      header: t('chatsTable.status.label'),
      size: SMALL_CELL_WIDTH,
    },
  );

  const autonomousAgentColumn = columnHelper.accessor('autonomous_agent_used', {
    Cell: ({ row }: Row) => {
      return row.original.autonomous_agent_used ? (
        <img
          alt={t('chatsTable.knowledgeRetrievalIconAlt')}
          height={24}
          src={autonomousAgentBadge}
          width={24}
        />
      ) : (
        <></>
      );
    },
    enableSorting: false,
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.autonomous_agent_used,
    size: 40,
  });

  // Transcript column — header label varies by mode ("Conversation" for
  // chat/web-call, "Inbound call"/"Outbound call" for voice mode tabs).
  const buildTranscriptColumn = (header: string) =>
    columnHelper.accessor('chat', {
      Cell: ({ row }: Row) => {
        return (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '8px',
              maxWidth: '540px',
              position: 'relative',
            }}
          >
            <ConversationTranscript transcript={row.original.chat} />
          </Box>
        );
      },
      enableSorting: false,
      header,
      size: 360,
    });

  const userQueriesColumn = columnHelper.accessor('user_queries', {
    Cell: ({ row }: Row) => {
      const userQueries = row.original.user_queries;

      if (userQueries.length === 1) {
        return (
          <CellContainer width='200px'>
            <i>
              <Typography color={palette.colors.blue[600]} variant='font14'>
                {t('chatsTable.quotedUserQuery', {
                  query: row.original.user_query,
                })}
              </Typography>
            </i>
          </CellContainer>
        );
      }

      if (userQueries.length > 1) {
        return (
          <OverflowDropdown
            data={userQueries}
            menuTitle={t('chatsTable.userQuery')}
            scope='workflow'
            tab='chat'
            variant='user_queries'
          />
        );
      }

      return (
        <Typography color={colorMapping.na} variant='font14'>
          {NA}
        </Typography>
      );
    },
    enableSorting: false,
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.user_queries,
    size: 200,
  });

  const deflectedColumn = columnHelper.accessor('is_deflected', {
    Cell: ({ row }: Row) => {
      if (row.original.voice_is_outbound) {
        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      }

      return (
        <Typography variant='font14'>
          {row.original.is_deflected ? t('chatsTable.yes') : t('chatsTable.no')}
        </Typography>
      );
    },
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.is_deflected,
    size: 130,
  });

  const eligibleColumn = columnHelper.accessor('visitor_interacted', {
    Cell: ({ row }: Row) => {
      const isOutbound = row.original.voice_is_outbound;
      const isEligible = isOutbound
        ? row.original.voice_outbound_answered_by === 'user'
        : row.original.visitor_interacted;

      if (isEligible === null || isEligible === undefined) {
        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      }

      return (
        <Typography variant='font14'>
          {isEligible ? (
            <FTBadge label={t('chatsTable.eligible')} variant='success' />
          ) : (
            <FTBadge label={t('chatsTable.ineligible')} variant='error' />
          )}
        </Typography>
      );
    },
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.visitor_interacted,
    Header: () => {
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '4px',
          }}
        >
          {TABLE_COLUMN_TO_LABEL_MAP.conversation.visitor_interacted}
          <Tooltip
            placement='top'
            tooltipContent={t('chatsTable.eligibleTooltip')}
          >
            <IconInfoCircle color={palette.colors.grey[700]} size={20} />
          </Tooltip>
        </Box>
      );
    },
    size: 190,
  });

  const customEventsColumn = isCustomMetricsEnabled
    ? columnHelper.accessor('custom_events', {
        Cell: ({ row }: Row) => {
          return row.original.custom_events.length > 0 ? (
            <OverflowDropdown
              data={uniqBy(
                'event_configuration_id',
                row.original.custom_events,
              )}
              menuTitle={t('chatsTable.customEventsFired')}
              scope={scope}
              tab='chat'
              variant='custom_events'
            />
          ) : (
            <Typography color={colorMapping.na} variant='font14'>
              {NA}
            </Typography>
          );
        },
        enableSorting: false,
        header: TABLE_COLUMN_TO_LABEL_MAP.conversation.custom_events,
        size: 230,
      })
    : null;

  const detectedAgentsCallColumn = buildDetectedIntentsColumn(
    t('chatsTable.agentsSurfacedInCall'),
  );

  // Talk time (voice call duration) — header label varies by mode.
  const buildTalkTimeColumn = (header: string) =>
    columnHelper.accessor('voice_call_duration', {
      Cell: ({ row }: Row) => {
        const callDuration = row.original.voice_call_duration;

        if (!callDuration) {
          return (
            <Typography color={colorMapping.na} variant='font14'>
              {NA}
            </Typography>
          );
        }

        return (
          <Typography variant='font14'>
            {formatTimeSpent(callDuration)}
          </Typography>
        );
      },
      header,
      size: 150,
    });

  const voiceCsatColumn =
    isVoiceCsatEnabled && !isVoiceModeView
      ? columnHelper.accessor('csat', {
          Cell: ({ row }: Row) => {
            if (!row.original.csat) {
              return (
                <Typography color={colorMapping.na} variant='font14'>
                  {NA}
                </Typography>
              );
            }
            const value = formatCSAT(row.original.csat);
            return (
              <Typography
                color={getCSATColor(Number(value))}
                variant='font14Medium'
              >
                {value}
              </Typography>
            );
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.csat,
          size: 100,
        })
      : null;

  const voiceSentimentColumn = columnHelper.accessor('voice_sentiment', {
    Cell: ({ row }: Row) => {
      const voiceSentiment = row.original.voice_sentiment;

      if (!voiceSentiment) {
        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      }

      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '4px',
          }}
        >
          <img src={getDiscoverSentimentEmoji(voiceSentiment, 'medium')} />
          <Typography variant='font14'>
            {capitalizeFirstLetter(voiceSentiment)}
          </Typography>
        </Box>
      );
    },
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.voice_sentiment,
    size: 150,
  });

  const voiceFromNumberColumn = columnHelper.accessor('voice_from_number', {
    Cell: ({ row }: Row) => {
      const fromNumber = row.original.voice_from_number;

      if (!fromNumber) {
        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      }

      return <Typography variant='font14'>{fromNumber}</Typography>;
    },
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.voice_from_number,
    size: 180,
  });

  // Outbound-only columns:
  const callAnsweredColumn = columnHelper.display({
    Cell: ({ row }: Row) => {
      const voiceIsOutbound = row.original.voice_is_outbound;
      if (!voiceIsOutbound) {
        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      }

      const voiceOutboundAnsweredBy = row.original.voice_outbound_answered_by;
      const { color: answeredColor, label: answeredDisplay } =
        voiceOutboundAnsweredBy === 'user'
          ? { color: palette.colors.green[500], label: t('chatsTable.yes') }
          : voiceOutboundAnsweredBy === 'voicemail'
            ? {
                color: palette.colors.purple[600],
                label: t('chatsTable.voicemail'),
              }
            : { color: palette.colors.red[500], label: t('chatsTable.no') };
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '4px',
          }}
        >
          <Typography color={answeredColor} variant='font14'>
            {answeredDisplay}
          </Typography>
        </Box>
      );
    },
    header: t('chatsTable.answered'),
    id: 'voice_outbound_answered',
    size: 150,
  });

  const outboundEngagedColumn = columnHelper.display({
    Cell: ({ row }: Row) => {
      const voiceIsOutbound = row.original.voice_is_outbound;

      if (!voiceIsOutbound) {
        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      }

      const { color: engagedColor, label: engagedDisplay } = row.original
        .voice_outbound_user_asked_followup
        ? {
            color: palette.colors.blue[400],
            label: t('chatsTable.followUpAsk'),
          }
        : row.original.voice_outbound_engaged
          ? { color: palette.colors.blue[700], label: t('chatsTable.engaged') }
          : {
              color: palette.colors.red[500],
              label: t('chatsTable.notEngaged'),
            };
      return (
        <Box
          sx={{
            backgroundColor: engagedColor,
            borderRadius: '4px',
            display: 'inline-flex',
            padding: '4px 8px',
          }}
        >
          <Typography color={palette.colors.white} noWrap variant='font12'>
            {engagedDisplay}
          </Typography>
        </Box>
      );
    },
    header: TABLE_COLUMN_TO_LABEL_MAP.conversation.voice_outbound_engaged,
    id: 'voice_outbound_engaged',
    size: 150,
  });

  const executedWorkflowColumn = columnHelper.accessor(
    'voice_outbound_workflow_name',
    {
      Cell: ({ row }: Row) => {
        const isOutbound = row.original.voice_is_outbound;
        const workflowName = row.original.voice_outbound_workflow_name;
        const workflowId = row.original.voice_outbound_workflow_id;
        if (!isOutbound || !workflowName || !workflowId) {
          return (
            <Typography color={colorMapping.na} variant='font14'>
              {NA}
            </Typography>
          );
        }
        return (
          <OverflowDropdown
            data={[
              {
                intent_id: workflowId,
                intent_name: workflowName,
                workflow_id: workflowId,
              },
            ]}
            menuTitle={t('chatsTable.workflowExecutedInCall')}
            scope='main'
            tab='workflow'
            variant='workflow'
          />
        );
      },
      header:
        TABLE_COLUMN_TO_LABEL_MAP.conversation.voice_outbound_workflow_name,
      size: 200,
    },
  );

  const voiceBrandColumn =
    isVoiceMultiBrandEnabled && !isWebCallChannel
      ? columnHelper.accessor('voice_to_number', {
          Cell: ({ row }: Row) => {
            const phoneNumber = row.original.voice_is_outbound
              ? row.original.voice_from_number
              : row.original.voice_to_number;

            if (!phoneNumber) {
              return (
                <Typography color={colorMapping.na} variant='font14'>
                  {NA}
                </Typography>
              );
            }

            return (
              <VoiceBrandBadge height='28px' phoneNumbers={[phoneNumber]} />
            );
          },
          header: t('chatsTable.brand'),
          size: 200,
        })
      : null;

  // Legacy combined-voice / web-call column set (order preserved).
  const voiceColumns = [
    detectedAgentsCallColumn,
    buildTalkTimeColumn(
      TABLE_COLUMN_TO_LABEL_MAP.conversation.voice_call_duration,
    ),
    ...(voiceCsatColumn ? [voiceCsatColumn] : []),
    voiceSentimentColumn,
    ...(isWebCallChannel || isVoiceModeView ? [] : [voiceFromNumberColumn]),
    ...(isOutboundMode
      ? [callAnsweredColumn, outboundEngagedColumn, executedWorkflowColumn]
      : []),
    ...(voiceBrandColumn ? [voiceBrandColumn] : []),
  ];

  const variantColumn = abTestConfigId
    ? columnHelper.display({
        Cell: ({ row }: Row) => {
          const samples = row.original.ab_test_samples;
          const sample = samples?.find(
            s => s.ab_test_config_id === abTestConfigId,
          );
          if (!sample) {
            return (
              <Typography color={colorMapping.na} variant='font14'>
                {NA}
              </Typography>
            );
          }
          const variantInfo = toABTestVariantInfo(sample);
          if (!variantInfo) {
            return (
              <Typography color={colorMapping.na} variant='font14'>
                {NA}
              </Typography>
            );
          }
          return <VariantPill size='medium' variant={variantInfo} />;
        },
        enableSorting: false,
        header: t('chatsTable.variant'),
        id: 'ab_test_variant',
        size: 150,
      })
    : null;

  const initialColumns = [
    timestampColumn,
    // Proactive vs. user-initiated origin. Non-voice only: the voice
    // channels have their own 'Type' column (inbound/outbound direction).
    showProactiveConversations && !isVoiceChannel && !isWebCallChannel
      ? columnHelper.accessor('conversation_type', {
          Cell: ({ row }: Row) => (
            <Typography variant='font14'>
              {row.original.conversation_type === 'proactive_conversation'
                ? t('chatsTable.source.proactive')
                : t('chatsTable.source.userInitiated')}
            </Typography>
          ),
          enableSorting: false,
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.conversation_type,
          size: SMALL_CELL_WIDTH,
        })
      : null,
    variantColumn,
    isVoiceChannel || isWebCallChannel
      ? null
      : columnHelper.accessor('widget_ticket_id', {
          Cell: ({ row }: Row) => {
            return (
              <Typography color={palette.colors.grey[700]} variant='font14'>
                {row.original.widget_ticket_id || NA}
              </Typography>
            );
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.widget_ticket_id,
          size: 250,
        }),
    isVoiceChannel ||
    isWebCallChannel ||
    isApiChannel ||
    isSlackChannel ||
    channelOptions.length < 2
      ? null
      : columnHelper.accessor('channel', {
          Cell: ({ row }: Row) => {
            const value = row.original.channel
              ? formatChannelLabel(row.original.channel)
              : NA;
            return <Typography variant='font14'>{value}</Typography>;
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.channel,
          size: SMALL_CELL_WIDTH,
        }),
    isResolutionUiEnabled ? resolutionAutomated : null,
    autonomousAgentColumn,

    buildTranscriptColumn(TABLE_COLUMN_TO_LABEL_MAP.conversation.chat),

    userQueriesColumn,

    isOutboundMode ? null : deflectedColumn,

    isResolutionUiEnabled ? resolutionTier : null,

    // Engagement of proactive conversations; n/a for user-initiated rows.
    showProactiveConversations && !isVoiceChannel && !isWebCallChannel
      ? columnHelper.accessor('proactive_engaged', {
          Cell: ({ row }: Row) => {
            if (row.original.conversation_type !== 'proactive_conversation') {
              return (
                <Typography color={colorMapping.na} variant='font14'>
                  {NA}
                </Typography>
              );
            }

            return (
              <Typography variant='font14'>
                {row.original.proactive_engaged
                  ? t('chatsTable.yes')
                  : t('chatsTable.no')}
              </Typography>
            );
          },
          enableSorting: false,
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.proactive_engaged,
          size: 150,
        })
      : null,

    isVoiceChannel || isWebCallChannel
      ? null
      : columnHelper.accessor(
          row => row.voice_call_duration || row.time_spent_in_widget,
          {
            Cell: ({ row }: Row) => {
              const timeSpent = row.original.time_spent_in_widget;

              if (!timeSpent) {
                return (
                  <Typography color={colorMapping.na} variant='font14'>
                    {NA}
                  </Typography>
                );
              }

              return (
                <Typography variant='font14'>
                  {formatTimeSpent(timeSpent)}
                </Typography>
              );
            },
            header: TABLE_COLUMN_TO_LABEL_MAP.conversation.time_spent_in_widget,
            id: 'time_spent_in_widget',
            size: 150,
          },
        ),

    isVoiceChannel && !isWebCallChannel && !channelMode
      ? columnHelper.accessor('voice_is_outbound', {
          Cell: ({ row }: Row) => {
            const voiceIsOutbound = row.original.voice_is_outbound;
            const voiceIsWebCall = row.original.voice_is_web_call;

            let callDirectionDisplay = voiceIsOutbound
              ? t('chatsTable.callDirection.outbound')
              : t('chatsTable.callDirection.inbound');

            // If it's an inbound web call, add the "- web call" suffix
            if (!voiceIsOutbound && voiceIsWebCall) {
              callDirectionDisplay = t(
                'chatsTable.callDirection.inboundWebCall',
              );
            }

            return (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                <Typography variant='font14'>
                  {capitalizeFirstLetter(callDirectionDisplay)}
                </Typography>
              </Box>
            );
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.voice_is_outbound,
          size: 150,
        })
      : null,

    isVoiceChannel && !isWebCallChannel ? eligibleColumn : null,

    customEventsColumn,
  ];

  const nonVoiceColumns = [
    buildDetectedIntentsColumn(t('chatsTable.agentsSurfacedInChat')),
    discoverAnalyticsEnabled && !isV3Taxonomy
      ? columnHelper.accessor('detected_topic', {
          Cell: ({ row }: Row) => {
            const navigate = useNavigate();
            if (row.original.detected_topic) {
              const { cluster_id, cluster_name } = row.original.detected_topic;
              return (
                <Box
                  onClick={() => {
                    const pathName = Routes.SOLVE_INSIGHTS_TOPIC_DETAIL.replace(
                      ':topicId',
                      cluster_id,
                    );
                    const queryParams = new URLSearchParams({
                      chats_sort_column: 'timestamp',
                    });

                    navigate({
                      pathname: pathName,
                      search: `?${queryParams}`,
                    });
                  }}
                  sx={{
                    '&:hover': { background: palette.colors.slate[200] },
                    background: palette.colors.slate[100],
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '3px',
                    padding: '4px 8px 4px 8px',
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      width: '17px',
                    }}
                  >
                    <IconBolt
                      color={palette.colors.green[500]}
                      fill={palette.colors.green[500]}
                      height={17}
                      strokeWidth={1}
                      width={17}
                    />
                  </Box>
                  <Typography color={palette.colors.grey[700]} variant='font12'>
                    {cluster_name}
                  </Typography>
                </Box>
              );
            }
            return (
              <Typography color={colorMapping.na} variant='font14'>
                {NA}
              </Typography>
            );
          },
          enableSorting: false,
          header: `${TABLE_COLUMN_TO_LABEL_MAP.conversation.detected_topic}${
            showInternalBadgeForAnalytics ? t('chatsTable.internalSuffix') : ''
          }`,
          Header: () => {
            const label = `${TABLE_COLUMN_TO_LABEL_MAP.conversation.detected_topic}${
              showInternalBadgeForAnalytics
                ? t('chatsTable.internalSuffix')
                : ''
            }`;
            return (
              <Box
                onMouseEnter={() => {
                  emitTrackingEventCallback('insight-tooltip-hover', {
                    scope,
                    tab,
                    table: 'chat',
                    value: 'detected_intents',
                  });
                }}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                {label}
                <Tooltip
                  tooltipContent={INSIGHT_TOOLTIP_COPY.chats_detected_topic}
                >
                  <IconInfoCircle color={palette.colors.grey[700]} size={20} />
                </Tooltip>
              </Box>
            );
          },
          size: showInternalBadgeForAnalytics ? 220 : 200,
        })
      : null,
    discoverAnalyticsEnabled && isV3Taxonomy
      ? columnHelper.accessor('detected_topics', {
          Cell: ({ row }: Row) => {
            if (!row.original.detected_topics?.length) {
              return (
                <Typography color={colorMapping.na} variant='font14'>
                  {NA}
                </Typography>
              );
            }
            return (
              <OverflowDropdown
                data={row.original.detected_topics ?? []}
                menuTitle={t('chatsTable.topicsDetected')}
                scope={scope}
                tab='chat'
                variant='topic'
              />
            );
          },
          enableSorting: false,
          header: `${TABLE_COLUMN_TO_LABEL_MAP.conversation.detected_topics}${
            showInternalBadgeForAnalytics ? t('chatsTable.internalSuffix') : ''
          }`,
          Header: () => {
            const label = `${TABLE_COLUMN_TO_LABEL_MAP.conversation.detected_topics}${
              showInternalBadgeForAnalytics
                ? t('chatsTable.internalSuffix')
                : ''
            }`;
            return (
              <Box
                onMouseEnter={() => {
                  emitTrackingEventCallback('insight-tooltip-hover', {
                    scope,
                    tab,
                    table: 'chat',
                    value: 'detected_intents',
                  });
                }}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                {label}
                <Tooltip
                  tooltipContent={INSIGHT_TOOLTIP_COPY.chats_detected_topic}
                >
                  <IconInfoCircle color={palette.colors.grey[700]} size={20} />
                </Tooltip>
              </Box>
            );
          },
          size: showInternalBadgeForAnalytics ? 220 : 200,
        })
      : null,

    columnHelper.accessor('articles', {
      Cell: ({ row }: Row) => {
        if (isDetail && row.original.articles.length) {
          return (
            <OverflowDropdown
              data={row.original.articles}
              menuTitle={t('chatsTable.articlesSurfaced')}
              scope={scope}
              tab='article'
              variant='article'
            />
          );
        }

        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      },
      enableSorting: false,
      header: TABLE_COLUMN_TO_LABEL_MAP.conversation.articles,
      Header: () => {
        return (
          <Box
            onMouseEnter={() => {
              emitTrackingEventCallback('insight-tooltip-hover', {
                scope,
                tab,
                table: 'chat',
                value: 'articles',
              });
            }}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '4px',
            }}
          >
            {TABLE_COLUMN_TO_LABEL_MAP.conversation.articles}
            <Tooltip
              tooltipContent={INSIGHT_TOOLTIP_COPY.chats_surfaced_articles}
            >
              <IconInfoCircle color={palette.colors.grey[700]} size={20} />
            </Tooltip>
          </Box>
        );
      },
      size: 260,
    }),
    shouldShowQuickFeedbackAnalytics ? quickFeedbackItem : null,
    isSlackChannel
      ? null
      : columnHelper.accessor('csat', {
          Cell: ({ row }: Row) => {
            if (!row.original.csat) {
              return (
                <Typography color={colorMapping.na} variant='font14'>
                  {NA}
                </Typography>
              );
            }
            const value = formatCSAT(row.original.csat);
            return (
              <Typography
                color={getCSATColor(Number(value))}
                variant='font14Medium'
              >
                {value}
              </Typography>
            );
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.csat,
          size: 100,
        }),
    isSentimentInsightsEnabled
      ? columnHelper.accessor('ending_sentiment_score', {
          Cell: ({ row }: Row) => {
            return (
              <SentimentInsightCell
                sentimentScore={row.original.ending_sentiment_score}
                timestamp={row.original.timestamp}
              />
            );
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.ending_sentiment_score,
          Header: () => {
            return (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '4px',
                }}
              >
                {TABLE_COLUMN_TO_LABEL_MAP.conversation.ending_sentiment_score}
                <Tooltip tooltipContent={<SentimentTooltip />}>
                  <IconInfoCircle color={palette.colors.grey[700]} size={20} />
                </Tooltip>
              </Box>
            );
          },
          size: 150,
        })
      : null,
    isApiChannel || isSlackChannel
      ? null
      : columnHelper.accessor('workflow_tag', {
          Cell: ({ row }: Row) => {
            const workflowTag = row.original.workflow_tag;
            if (workflowTag) {
              return (
                <PillButton
                  maxWidth='100%'
                  onClick={() => {
                    return;
                  }}
                  variant='workflowTag'
                >
                  <Box
                    sx={{
                      '& a': { color: 'inherit', textDecoration: 'none' },
                      alignItems: 'center',
                      display: 'flex',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: '4px',
                      }}
                    >
                      <IconTag color={palette.colors.blue[500]} size={16} />
                      <Box
                        sx={{
                          color: 'inherit',
                          display: 'flex',
                          overflow: 'hidden',
                          textDecoration: 'none',
                          textOverflow: 'ellipsis',
                          width: '100%',
                        }}
                      >
                        <Typography
                          color={palette.colors.black}
                          noWrap
                          variant='font14'
                        >
                          {workflowTag}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </PillButton>
              );
            }

            return (
              <Typography color={palette.colors.grey[500]} variant='font14'>
                {NA}
              </Typography>
            );
          },
          enableSorting: false,
          header: TABLE_COLUMN_TO_LABEL_MAP.conversation.workflow_tag,
          size: 160,
        }),
    isDeflectionInsightsEnabled && !isApiChannel && !isSlackChannel
      ? relevanceRating
      : null,
    isDeflectionInsightsEnabled && !isApiChannel && !isSlackChannel
      ? dropoffRating
      : null,
  ];

  // AI QA score columns - one column per rubric name
  const aiQaColumns =
    hasAiQaPermission && aiQaRubricNames
      ? aiQaRubricNames.map(rubricName =>
          columnHelper.display({
            Cell: ({ row }: Row) => {
              const aiQaScores = row.original.ai_qa_scores;
              const scoreItem = aiQaScores?.find(
                s => s.rubric_name === rubricName,
              );

              if (!scoreItem || scoreItem.score === null) {
                return (
                  <Typography color={colorMapping.na} variant='font14'>
                    {NA}
                  </Typography>
                );
              }

              // Display label if it exists, otherwise fall back to score
              const displayValue = scoreItem.label
                ? capitalizeFirstLetter(scoreItem.label)
                : scoreItem.score;

              return <Typography variant='font14'>{displayValue}</Typography>;
            },
            header: rubricName,
            id: `ai_qa_${rubricName.split(' ').join('_').toLowerCase()}`,
            size: 200,
          }),
        )
      : [];

  const reviewStatusColumn =
    isHumanEvalsEnabled && isReviewEnabledChannel
      ? columnHelper.display({
          Cell: ({ row }: Row) => {
            return (
              <ReviewStatusIndicator
                isFlagged={row.original.is_flagged}
                reviewStatus={row.original.review_status}
              />
            );
          },
          enableSorting: false,
          header: '',
          id: 'review_status',
          size: 56,
        })
      : null;

  // Reuse the already-built detected-topic column (V2/V3) for voice inbound.
  const detectedTopicColumn =
    nonVoiceColumns.find(
      col =>
        col != null &&
        'accessorKey' in col &&
        (col.accessorKey === 'detected_topic' ||
          col.accessorKey === 'detected_topics'),
    ) ?? null;

  const relevanceColumn = isDeflectionInsightsEnabled ? relevanceRating : null;

  // Explicit, mock-ordered column lists for the voice mode tabs (image-2 /
  // image-5). Null entries (disabled flags) are dropped by the final filter.
  // The trailing outbound column (Executed workflow) is not in the mock but
  // kept as extra detail.
  const voiceInboundColumns = [
    timestampColumn,
    variantColumn,
    // The resolution UI folds call eligibility into the Automated column.
    isResolutionUiEnabled ? null : eligibleColumn,
    isResolutionUiEnabled ? voiceResolutionAutomated : null,
    autonomousAgentColumn,
    buildTranscriptColumn(transcriptLabel),
    userQueriesColumn,
    buildTalkTimeColumn(talkTimeLabel),
    deflectedColumn,
    isResolutionUiEnabled ? resolutionTier : null,
    customEventsColumn,
    detectedAgentsCallColumn,
    voiceSentimentColumn,
    relevanceColumn,
    voiceBrandColumn,
    detectedTopicColumn,
  ];

  const voiceOutboundColumns = [
    timestampColumn,
    variantColumn,
    // The resolution UI folds call eligibility into the Automated column and
    // hides resolutions for outbound calls.
    isResolutionUiEnabled ? null : eligibleColumn,
    isResolutionUiEnabled ? voiceResolutionAutomated : null,
    autonomousAgentColumn,
    buildTranscriptColumn(transcriptLabel),
    userQueriesColumn,
    callAnsweredColumn,
    outboundEngagedColumn,
    buildTalkTimeColumn(talkTimeLabel),
    customEventsColumn,
    detectedAgentsCallColumn,
    voiceSentimentColumn,
    relevanceColumn,
    voiceBrandColumn,
    executedWorkflowColumn,
  ];

  const resultColumns = isVoiceModeTab
    ? [
        ...(isOutboundMode ? voiceOutboundColumns : voiceInboundColumns),
        ...aiQaColumns,
        reviewStatusColumn,
      ]
    : isVoiceChannel || isWebCallChannel
      ? [
          ...initialColumns,
          ...voiceColumns,
          // Legacy combined voice keeps Relevance (both modes) and Detected
          // topic (inbound only); web call keeps its existing set.
          ...(isVoiceModeView && isDeflectionInsightsEnabled
            ? [relevanceRating]
            : []),
          ...(isVoiceModeView && !isOutboundMode ? [detectedTopicColumn] : []),
          ...aiQaColumns,
          reviewStatusColumn,
        ]
      : [
          ...initialColumns,
          ...nonVoiceColumns,
          ...aiQaColumns,
          reviewStatusColumn,
          conversationStatusColumn,
        ];

  return resultColumns.filter((item): item is Item => item !== null);
};

export const buildEmailConversationColumns = (
  t: TFunction<'solve-insights'>,
  palette: Palette,
  scope: Scope,
  setIsUnansweredCausesDialogOpen: (isOpen: boolean) => void,
  navigate: NavigateFunction,
  shouldShowZendeskCsatColumn = false,
  isClassicWorkflowsDeprecated = false,
  isResolutionUiEnabled = false,
) => {
  const colorMapping = colorMap(palette);
  const handleClickLink = (intentId: string, workflowId = '') => {
    const searchParams = new URLSearchParams({
      intentId: intentId,
      mode: getEffectiveWorkflowMode(
        intentId,
        false,
        isClassicWorkflowsDeprecated,
      ),
      view: 'email',
      workflowId: workflowId,
    });
    const pathName = Routes.WORKFLOW_BUILDER_EDIT;

    navigate({
      pathname: pathName,
      search: searchParams.toString(),
    });
  };

  const columnHelper = createMRTColumnHelper<InsightEmailConversation>();
  const initialColumns = [
    columnHelper.accessor('timestamp', {
      Cell: ({ row }: EmailRow) => {
        return (
          <Box
            sx={{
              width: SMALL_CELL_WIDTH,
            }}
          >
            <DateCell dateString={row.original.timestamp + 'Z'} />
          </Box>
        );
      },
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.timestamp,
      size: SMALL_CELL_WIDTH,
    }),
    isResolutionUiEnabled
      ? columnHelper.display({
          Cell: ({ row }: EmailRow) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: '100%',
                }}
              >
                <ResolutionAutomatedLabel
                  resolutionAutomated={row.original.resolution_automated}
                />
              </Box>
            );
          },
          enableSorting: false,
          header:
            TABLE_COLUMN_TO_LABEL_MAP.email_conversation.resolution_automated,
          id: 'resolution_automated',
          size: AUTOMATED_CELL_WIDTH,
        })
      : null,
    columnHelper.accessor('ticket_id', {
      Cell: ({ row }: EmailRow) => {
        return (
          <Typography variant='font14'>{row.original.ticket_id}</Typography>
        );
      },
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.ticket_id,
      size: 175,
    }),
    columnHelper.accessor('ticket_body', {
      Cell: ({ row }: EmailRow) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              maxWidth: 540,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                mb: '4px',
              }}
            >
              {row.original.used_human_in_the_loop && (
                <WorkflowBadge type='hitl' />
              )}
              {row.original.is_interactive_email && (
                <WorkflowBadge type='interactive' />
              )}
              {row.original.is_autoflow && <WorkflowBadge type='autoflows' />}
            </Box>
            <Box
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
              }}
            >
              <Typography variant='font14Bold'>
                {row.original.ticket_title}
              </Typography>
            </Box>
            <Box
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
              }}
            >
              <Typography color={palette.colors.grey[500]} variant='font14'>
                {row.original.ticket_body}
              </Typography>
            </Box>
          </Box>
        );
      },
      enableSorting: false,
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.ticket_body,
      size: 440,
    }),
    columnHelper.accessor('is_deflected', {
      Cell: ({ row }: EmailRow) => {
        const value = row.original.is_deflected
          ? t('chatsTable.yes')
          : t('chatsTable.no');
        return <Typography variant='font14'>{value}</Typography>;
      },
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.is_deflected,
      size: 150,
    }),
    isResolutionUiEnabled
      ? columnHelper.accessor('resolution_tier', {
          Cell: ({ row }: EmailRow) => {
            return (
              <ResolutionTierBadge
                resolutionTier={row.original.resolution_tier}
              />
            );
          },
          enableSorting: false,
          header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.resolution_tier,
          size: 175,
        })
      : null,
    isResolutionUiEnabled
      ? null
      : columnHelper.accessor('is_eligible', {
          Cell: ({ row }: EmailRow) => {
            const value = row.original.is_eligible
              ? t('chatsTable.yes')
              : t('chatsTable.no');
            return <Typography variant='font14'>{value}</Typography>;
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.is_eligible,
          size: 150,
        }),
    isResolutionUiEnabled
      ? null
      : columnHelper.accessor('response_sent', {
          Cell: ({ row }: EmailRow) => {
            const value = row.original.response_sent
              ? t('chatsTable.yes')
              : t('chatsTable.no');
            return <Typography variant='font14'>{value}</Typography>;
          },
          header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.response_sent,
          size: 150,
        }),
    columnHelper.accessor('skip_reason', {
      Cell: ({ row }: EmailRow) => {
        const allSkipReasons = [
          row.original.skip_reason,
          row.original.email_workflow_execution_status ?? 'no_value',
        ]
          .filter(reason => reason !== 'finished')
          .map(reason => getReadableNoResponseString(reason));
        const isEmpty =
          allSkipReasons.length === 0 ||
          (allSkipReasons.length === 1 && allSkipReasons[0] === '');
        if (isEmpty) {
          return (
            <Typography color={colorMapping.na} variant='font14'>
              {NA}
            </Typography>
          );
        }
        return <Typography variant='font14'>{allSkipReasons}</Typography>;
      },
      enableSorting: false,
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.skip_reason,
      Header: () => {
        return (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '4px',
            }}
          >
            {TABLE_COLUMN_TO_LABEL_MAP.email_conversation.skip_reason}
            <Box
              onClick={() => setIsUnansweredCausesDialogOpen(true)}
              sx={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <Tooltip tooltipContent={false}>
                <IconInfoCircle color={palette.colors.grey[700]} size={20} />
              </Tooltip>
            </Box>
          </Box>
        );
      },
      size: 260,
    }),
    columnHelper.accessor('detected_intents', {
      Cell: ({ row }: EmailRow) => {
        if (row.original.detected_intents.length === 0) {
          return (
            <Typography color={colorMapping.na} variant='font14'>
              {NA}
            </Typography>
          );
        }

        return (
          <OverflowDropdown
            data={row.original.detected_intents}
            menuTitle={t('chatsTable.agentsExecuted')}
            onItemClick={handleClickLink}
            scope={scope}
            tab='chat'
            variant='workflow'
          />
        );
      },
      enableSorting: false,
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.detected_intents,
      Header: () => {
        return (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '4px',
            }}
          >
            {TABLE_COLUMN_TO_LABEL_MAP.email_conversation.detected_intents}
            <Tooltip tooltipContent={INSIGHT_TOOLTIP_COPY.chats_workflows}>
              <IconInfoCircle color={palette.colors.grey[700]} size={20} />
            </Tooltip>
          </Box>
        );
      },
      size: 260,
    }),
    columnHelper.accessor('articles', {
      Cell: ({ row }: EmailRow) => {
        if (row.original.articles.length) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '200px',
              }}
            >
              {row.original.articles.map(article => (
                <a
                  href={article.article_link}
                  key={article.article_id}
                  rel='noreferrer'
                  target='_blank'
                >
                  <Tooltip tooltipContent={article.article_title}>
                    <Box
                      sx={{
                        '&:hover': {
                          bgcolor: palette.colors.slate[200],
                          cursor: 'pointer',
                        },
                        overflow: 'hidden',
                        textDecoration: 'underline',
                        textOverflow: 'ellipsis',
                        width: '200px',
                      }}
                    >
                      <Typography
                        color={palette.colors.blue[600]}
                        noWrap
                        variant='font12Medium'
                      >
                        {article.article_title}
                      </Typography>
                    </Box>
                  </Tooltip>
                </a>
              ))}
            </Box>
          );
        }

        return (
          <Typography color={colorMapping.na} variant='font14'>
            {NA}
          </Typography>
        );
      },
      enableSorting: false,
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.articles,
      Header: () => {
        return (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '4px',
            }}
          >
            {TABLE_COLUMN_TO_LABEL_MAP.email_conversation.articles}
            <Tooltip
              tooltipContent={INSIGHT_TOOLTIP_COPY.chats_surfaced_articles}
            >
              <IconInfoCircle color={palette.colors.grey[700]} size={20} />
            </Tooltip>
          </Box>
        );
      },
      size: 260,
    }),
    columnHelper.accessor('user_feedback', {
      Cell: ({ row }: EmailRow) => {
        const value = row.original.user_feedback;
        let icon = '';
        if (value === 'positive') {
          icon = thumbUp;
        } else if (value === 'negative') {
          icon = thumbDown;
        }
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '150px',
            }}
          >
            {icon ? (
              <img src={icon} style={{ height: '18px', width: '18px' }} />
            ) : (
              <Typography color={colorMapping.na} variant='font14'>
                {NA}
              </Typography>
            )}
          </Box>
        );
      },
      header: TABLE_COLUMN_TO_LABEL_MAP.email_conversation.user_feedback,
      size: 175,
    }),
    ...(shouldShowZendeskCsatColumn
      ? [
          columnHelper.accessor('zendesk_csat_score', {
            Cell: ({ row }: EmailRow) => {
              const value = row.original.zendesk_csat_score;
              let displayValue = t('chatsTable.zendeskCsat.na');
              let color = palette.colors.grey[500];

              if (value === 'good') {
                displayValue = t('chatsTable.zendeskCsat.good');
                color = palette.colors.green[500];
              } else if (value === 'bad') {
                displayValue = t('chatsTable.zendeskCsat.bad');
                color = palette.colors.red[500];
              }

              return (
                <Typography color={color} variant='font14'>
                  {displayValue}
                </Typography>
              );
            },
            header:
              TABLE_COLUMN_TO_LABEL_MAP.email_conversation.zendesk_csat_score,
            size: 175,
          }),
        ]
      : []),
  ];

  return initialColumns.filter(
    (column): column is Exclude<(typeof initialColumns)[number], null> =>
      column !== null,
  );
};

type Item =
  | MRT_ColumnDef<InsightConversation, string>
  | MRT_ColumnDef<InsightConversation, string[]>
  | MRT_ColumnDef<InsightConversation, boolean>;

type Row = { row: MRT_Row<InsightConversation> };
type EmailRow = { row: MRT_Row<InsightEmailConversation> };

export const getTableHeight = (view: View) => {
  switch (view) {
    case 'chats':
      return 250;
    case 'topics':
      return 300;
    case 'articles':
      return 300;
    case 'workflows':
      return 300;
    case 'ab_tests':
      return 270;
    default:
      return 250;
  }
};

export const assistFilterCount = (multiSelected: string[]) => {
  const total = multiSelected.length;
  if (!total) {
    return null;
  }

  const interacted = multiSelected.find(filter =>
    filter.includes('interacted'),
  );
  const generatedReply = multiSelected.find(filter =>
    filter.includes('generated_reply'),
  );
  const workflowId = multiSelected.find(filter =>
    filter.includes('workflow_id'),
  );
  const agentEmailSet = multiSelected.find(filter =>
    filter.includes('agent_email_set'),
  );

  let count = 0;
  if (workflowId) {
    count++;
  }
  if (interacted) {
    count++;
  }
  if (generatedReply) {
    count++;
  }
  if (agentEmailSet) {
    count++;
  }
  return count;
};

export const createAssistFilterOptions = (filters: string[]) => {
  const filterOptions: Pick<
    AssistBaseFilterRequest,
    'agent_email_set' | 'generated_reply' | 'interacted' | 'workflow_id'
  > = {
    agent_email_set: null,
    generated_reply: null,
    interacted: null,
    workflow_id: null,
  };

  // Group filters by their type
  const groupedFilters = filters.reduce(
    (acc, filter) => {
      const [type, value] = filter.split('.');
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(value);
      return acc;
    },
    {} as Record<string, string[]>,
  );

  // Process each filter type
  Object.entries(groupedFilters).forEach(([type, values]) => {
    if (type === 'workflow_id') {
      filterOptions.workflow_id = values.join(',');
      return;
    }
    // For other types, check if there are conflicting values; only apply
    // when a single value was chosen so we don't try to combine incompatible picks.
    const uniqueValues = [...new Set(values)];
    if (uniqueValues.length !== 1) {
      filterOptions[type as keyof typeof filterOptions] = null;
      return;
    }
    const [value] = uniqueValues;
    if (type === 'generated_reply') {
      filterOptions.generated_reply = value as 'used' | 'not_used' | null;
    } else if (type === 'interacted') {
      filterOptions.interacted = value as 'yes' | 'no' | null;
    } else if (type === 'agent_email_set') {
      filterOptions.agent_email_set = value as 'yes' | 'no' | null;
    }
  });

  return filterOptions;
};

export const serializeSorting = (
  sorting: {
    desc: boolean;
    id: string;
  }[],
): string => {
  const sortingItem = sorting[0];
  if (!sortingItem) {
    return '';
  }
  return `${sortingItem.id}:${sortingItem.desc ? 'desc' : 'asc'}`;
};

export const deserializeSorting = (
  sorting: string,
): {
  desc: boolean;
  id: string;
}[] => {
  const [id, desc] = sorting.split(':');
  return [{ desc: desc === 'desc', id }];
};

export const getSortColumn = (sortingColumn: string) => {
  if (sortingColumn === 'timestamp') {
    return 'start_datetime';
  }
  return sortingColumn;
};
